/**
 * Back office → Bible (C1): read the published version, edit a draft section by section,
 * see the changes, publish (P1-02), restore an old version (P1-03).
 */

import {
  type KnowledgeSection,
  type KnowledgeVersion,
  useKnowledgeSectionsCreate,
  useKnowledgeSectionsDestroy,
  useKnowledgeSectionsPartialUpdate,
  useKnowledgeVersionsCompareList,
  useKnowledgeVersionsDraftCreate,
  useKnowledgeVersionsList,
  useKnowledgeVersionsPublishCreate,
  useKnowledgeVersionsPublishedRetrieve,
  useKnowledgeVersionsRestoreCreate,
  useKnowledgeVersionsRetrieve,
} from "@agencyos/api-client";
import { Badge, Button, EmptyState, GlassCard, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { BookOpen } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../components/AppShell";
import { fieldErrors, useCanEditSettings, useCurrentRole } from "../lib/settings";
import { Pager } from "./billing/shared";

function useDate() {
  const { i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString(locale, { timeZone: "Africa/Douala" }) : "";
}

/** Every bible query (versions, draft, published, compare): their URLs start the same way. */
function useRefreshBible() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/knowledge/"),
    });
}

function ReadSection({ section }: { section: KnowledgeSection }) {
  const { t } = useTranslation();
  return (
    <GlassCard as="section" className="stack bible-section" id={`bible-${section.id}`}>
      <div className="row between">
        <h2>{section.title}</h2>
        <div className="row">
          {section.owner_only ? <Badge tone="offer">{t("bible.ownerOnly")}</Badge> : null}
          {section.for_ai === false ? <Badge>{t("bible.internal")}</Badge> : null}
        </div>
      </div>
      <div className="bible-text" lang="fr">
        {section.content}
      </div>
    </GlassCard>
  );
}

function EditSection({ section, isOwner }: { section: KnowledgeSection; isOwner: boolean }) {
  const { t } = useTranslation();
  const refresh = useRefreshBible();
  const [title, setTitle] = useState(section.title);
  const [content, setContent] = useState(section.content ?? "");
  const [forAi, setForAi] = useState(section.for_ai ?? true);
  const update = useKnowledgeSectionsPartialUpdate({ mutation: { onSuccess: refresh } });
  const remove = useKnowledgeSectionsDestroy({ mutation: { onSuccess: refresh } });
  const locked = Boolean(section.owner_only) && !isOwner;
  const errors = fieldErrors(update.error);
  return (
    <GlassCard as="section" className="stack">
      {section.owner_only ? (
        <p className="muted">
          <Badge tone="offer">{t("bible.ownerOnly")}</Badge> {t("bible.ownerOnlyHint")}
        </p>
      ) : null}
      <fieldset className="plain-fieldset stack" disabled={locked}>
        <TextField label={t("bible.sectionTitle")} value={title} error={errors.title} onChange={(e) => setTitle(e.target.value)} />
        <TextAreaField
          label={t("bible.content")}
          lang="fr"
          rows={Math.min(24, Math.max(6, content.split("\n").length + 1))}
          value={content}
          error={errors.content}
          onChange={(e) => setContent(e.target.value)}
        />
        <SoftToggle label={t("bible.forAi")} checked={forAi} onChange={setForAi} />
        <div className="row">
          <Button
            variant="secondary"
            disabled={update.isPending}
            onClick={() => update.mutate({ id: section.id, data: { title, content, for_ai: forAi } })}
          >
            {update.isPending ? t("common.loading") : t("bible.saveSection")}
          </Button>
          <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate({ id: section.id })}>
            {t("bible.deleteSection")}
          </Button>
          {update.isSuccess ? (
            <span role="status" className="ok">
              {t("common.saved")}
            </span>
          ) : null}
        </div>
      </fieldset>
    </GlassCard>
  );
}

function Changes({ draft }: { draft: KnowledgeVersion }) {
  const { t } = useTranslation();
  const compare = useKnowledgeVersionsCompareList(draft.id, undefined, { query: { staleTime: 0 } });
  if (compare.data?.status !== 200) return null;
  const changes = compare.data.data;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("bible.changes")}</h2>
      {changes.length === 0 ? <p className="muted">{t("bible.noChanges")}</p> : null}
      {changes.map((change) => (
        <div key={change.key} className="stack">
          <h3>
            {change.title} <Badge tone={change.change === "removed" ? "danger" : "success"}>{t(`bible.change.${change.change}`)}</Badge>
          </h3>
          <pre className="diff" lang="fr">
            {change.lines.map((line, index) => (
              <span
                key={index}
                className={line.startsWith("+") ? "diff__add" : line.startsWith("-") ? "diff__del" : "diff__ctx"}
              >
                {line}
                {"\n"}
              </span>
            ))}
          </pre>
        </div>
      ))}
    </GlassCard>
  );
}

function DraftEditor({ draftId }: { draftId: string }) {
  const { t } = useTranslation();
  const refresh = useRefreshBible();
  const role = useCurrentRole();
  const draft = useKnowledgeVersionsRetrieve(draftId);
  const add = useKnowledgeSectionsCreate({ mutation: { onSuccess: refresh } });
  const publish = useKnowledgeVersionsPublishCreate({ mutation: { onSuccess: refresh } });
  const [note, setNote] = useState("");
  if (draft.data?.status !== 200)
    return (
      <GlassCard aria-busy="true">
        <Skeleton />
      </GlassCard>
    );
  const version = draft.data.data;
  return (
    <>
      <GlassCard role="status" className="notice-warning">
        {t("bible.draftBanner", { number: version.number })}
      </GlassCard>
      {version.sections.map((section) => (
        <EditSection key={section.id} section={section} isOwner={role === "owner"} />
      ))}
      <div className="row">
        <Button
          variant="secondary"
          disabled={add.isPending}
          onClick={() => add.mutate({ data: { title: t("bible.newSectionTitle"), content: "", order: version.sections.length + 1 } })}
        >
          {t("bible.addSection")}
        </Button>
      </div>
      <Changes draft={version} />
      <GlassCard className="stack sticky-actions">
        <TextField label={t("bible.changeNote")} value={note} maxLength={255} onChange={(e) => setNote(e.target.value)} />
        <div className="row">
          <Button disabled={publish.isPending} onClick={() => publish.mutate({ id: version.id, data: { change_note: note } })}>
            {t("bible.publish")}
          </Button>
        </div>
      </GlassCard>
    </>
  );
}

function History() {
  const { t } = useTranslation();
  const date = useDate();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshBible();
  const [page, setPage] = useState(1);
  const versions = useKnowledgeVersionsList({ page }); // D-115: every version, page by page
  const restore = useKnowledgeVersionsRestoreCreate({ mutation: { onSuccess: refresh } });
  if (versions.data?.status !== 200) return null;
  const hasNext = Boolean(versions.data.data.next);
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("bible.history")}</h2>
      <ul className="plain-list">
        {versions.data.data.results.map((version) => (
          <li key={version.id} className="row between">
            <span>
              <strong>v{version.number}</strong> <Badge tone={version.status === "published" ? "success" : "default"}>{t(`bible.status.${version.status}`)}</Badge>{" "}
              <span className="muted">
                {date(version.published_at ?? version.created_at)}
                {version.change_note ? ` · ${version.change_note}` : ""}
                {version.restored_from_number ? ` · ${t("bible.restored", { number: version.restored_from_number })}` : ""}
              </span>
            </span>
            {canEdit && version.status === "archived" ? (
              <Button variant="ghost" disabled={restore.isPending} onClick={() => restore.mutate({ id: version.id })}>
                {t("bible.restore")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {page > 1 || hasNext ? (
        <Pager
          page={page}
          hasNext={hasNext}
          onChange={setPage}
          labels={{ previous: t("billing.previous"), next: t("billing.next"), page: t("billing.page", { page }) }}
        />
      ) : null}
    </GlassCard>
  );
}

export function BiblePage() {
  const { t } = useTranslation();
  const date = useDate();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshBible();
  const published = useKnowledgeVersionsPublishedRetrieve();
  const versions = useKnowledgeVersionsList();
  const openDraft = useKnowledgeVersionsDraftCreate({ mutation: { onSuccess: refresh } });
  const draftId =
    versions.data?.status === 200 ? versions.data.data.results.find((v) => v.status === "draft")?.id : undefined;
  const current = published.data?.status === 200 ? published.data.data : undefined;

  let body;
  if (published.isPending || versions.isPending)
    body = (
      <GlassCard aria-busy="true">
        <Skeleton />
      </GlassCard>
    );
  else if (draftId && canEdit) body = <DraftEditor draftId={draftId} />;
  else if (current) body = current.sections.map((section) => <ReadSection key={section.id} section={section} />);
  else body = <EmptyState icon={<BookOpen size={22} strokeWidth={1.75} />} title={t("bible.none")} />;
  const reading = Boolean(current && !(draftId && canEdit));

  return (
    <Page
      title={t("bible.title")}
      description={
        <>
          {t("bible.intro")}
          {canEdit ? null : <> {t("common.readOnly")}</>}
        </>
      }
      actions={
        <>
          {current ? <Badge dot tone="success">{t("bible.published", { number: current.number, date: date(current.published_at) })}</Badge> : null}
          {canEdit && !draftId ? (
            <Button disabled={openDraft.isPending} onClick={() => openDraft.mutate()}>
              {t("bible.openDraft")}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="bible-layout">
        <aside className="bible-layout__aside">
          {reading && current ? (
            <nav className="ao-glass ao-card bible-toc" aria-label={t("bible.contents")}>
              <p className="bible-toc__title">{t("bible.contents")}</p>
              <ol className="plain-list">
                {current.sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#bible-${section.id}`} className="bible-toc__link">
                      <span className="ao-num">{index + 1}</span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          ) : null}
          <History />
        </aside>
        <div className="bible-layout__main stack">{body}</div>
      </div>
    </Page>
  );
}
